/**
 * Admin V3 — Pricing — BandsEditor
 *
 * Editable 3-band tariff editor for Stage C Schedule A (Gate A canonical):
 *   - Band 0 (≤ 5 km, flat 20 €): upper_km + flat_fare editable
 *   - Band 1 (5–10 km, flat 30 €): upper_km + flat_fare editable
 *   - Band 2 (> 10 km, additive 30 € + 2.20 €/km after 10 km):
 *       additive_base + additive_rate_per_km + additive_lower_km editable;
 *       upper_km is forced to null (open tail) and not shown.
 *
 * SCOPE (Phase 1 binding):
 *   - This component does not commit or fetch anything; it is a pure
 *     controlled form. Parent (PricingConfigPage) owns state and submission.
 *   - The 3-band SHAPE is fixed; users cannot add or remove bands.
 *   - The MODE of each band is fixed (band 0/1 = flat, band 2 = additive).
 *
 * Read-only mode: when canEdit=false, inputs render as disabled and the
 * component looks like a labelled summary. RBAC HIDE posture defers the
 * authoritative check to the backend.
 */

import React from 'react';
import { Layers, AlertTriangle } from 'lucide-react';
import { SectionCard, cn } from '../../shared/visualTokens';

const BAND_LABEL = {
  fr: {
    title:        'Tarif par bandes (Schedule A)',
    band0:        'Bande 0 — Forfait court (≤ borne supérieure)',
    band1:        'Bande 1 — Forfait moyen (au-delà de la bande 0)',
    band2:        'Bande 2 — Tarif additif (au-delà de la bande 1)',
    upperKm:      'Borne supérieure (km)',
    flatFare:     'Forfait (€)',
    additiveBase:        'Base additive (€)',
    additiveRatePerKm:   'Tarif additif (€/km)',
    additiveLowerKm:     'Seuil km où le tarif additif commence',
    openTail:     'Ouvert (jusqu\'à l\'infini)',
    hint:         'Les modifications de bande affectent tous les nouveaux devis client. Schedule A est la source de vérité ; la configuration héritée ci-dessous est décorative.',
  },
};

const labels = BAND_LABEL.fr;

const Field = ({ label, value, onChange, error, disabled, type = 'number', step = '0.01', min, suffix, testId }) => (
  <div className="flex flex-col">
    <label className="mb-1 block text-[12px] font-semibold text-slate-700">{label}</label>
    <div className="relative">
      <input
        type={type}
        step={step}
        min={min}
        value={value === null || value === undefined ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          'w-full rounded-xl border bg-white px-3 py-2 pr-10 text-[13px] text-slate-900 outline-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
          error ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-emerald-400',
        )}
        data-testid={testId}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-slate-400">
          {suffix}
        </span>
      )}
    </div>
    {error && (
      <span className="mt-1 text-[11px] text-rose-600" data-testid={`${testId}-error`}>
        {error}
      </span>
    )}
  </div>
);

const ReadOnlyChip = ({ label, value, testId }) => (
  <div className="flex flex-col">
    <span className="mb-1 text-[12px] font-semibold text-slate-700">{label}</span>
    <span
      className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-700"
      data-testid={testId}
    >
      {value}
    </span>
  </div>
);

const BandSection = ({ title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <h4 className="mb-3 text-[13px] font-semibold text-slate-800">{title}</h4>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{children}</div>
  </div>
);

const parseDecimal = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const normalised = String(raw).replace(',', '.').trim();
  const n = Number(normalised);
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Props:
 *   bands         — array of 3 band objects (controlled)
 *   onChange(next)— receives an updated bands array
 *   errors        — flat error map keyed by dotted path (e.g. 'bands.0.flat_fare')
 *   canEdit       — when false, all inputs disabled
 */
const BandsEditor = ({ bands, onChange, errors, canEdit }) => {
  const safeBands = Array.isArray(bands) && bands.length === 3
    ? bands
    : [
        { upper_km: 5,   mode: 'flat',     flat_fare: 20, additive_base: null, additive_rate_per_km: null, additive_lower_km: null },
        { upper_km: 10,  mode: 'flat',     flat_fare: 30, additive_base: null, additive_rate_per_km: null, additive_lower_km: null },
        { upper_km: null, mode: 'additive', flat_fare: null, additive_base: 30, additive_rate_per_km: 2.20, additive_lower_km: 10 },
      ];

  const updateField = (index, field, raw) => {
    const parsed = parseDecimal(raw);
    const next = safeBands.map((b, i) => (i === index ? { ...b, [field]: Number.isNaN(parsed) ? raw : parsed } : b));
    onChange(next);
  };

  const err = (k) => (errors && errors[k]) || '';

  return (
    <SectionCard title={labels.title} icon={Layers} iconClass="bg-emerald-100 text-emerald-600">
      <div className="space-y-3" data-testid="adminv3-pricing-bands-editor">
        {/* Band 0 — Flat short */}
        <BandSection title={labels.band0}>
          <Field
            label={labels.upperKm}
            value={safeBands[0]?.upper_km}
            onChange={(v) => updateField(0, 'upper_km', v)}
            error={err('bands.0.upper_km') || err('bands.upper_km_order')}
            disabled={!canEdit}
            suffix="km"
            testId="adminv3-pricing-band-0-upper_km"
          />
          <Field
            label={labels.flatFare}
            value={safeBands[0]?.flat_fare}
            onChange={(v) => updateField(0, 'flat_fare', v)}
            error={err('bands.0.flat_fare')}
            disabled={!canEdit}
            suffix="€"
            testId="adminv3-pricing-band-0-flat_fare"
          />
          <ReadOnlyChip label="Mode" value="flat" testId="adminv3-pricing-band-0-mode" />
        </BandSection>

        {/* Band 1 — Flat medium */}
        <BandSection title={labels.band1}>
          <Field
            label={labels.upperKm}
            value={safeBands[1]?.upper_km}
            onChange={(v) => updateField(1, 'upper_km', v)}
            error={err('bands.1.upper_km') || err('bands.upper_km_order')}
            disabled={!canEdit}
            suffix="km"
            testId="adminv3-pricing-band-1-upper_km"
          />
          <Field
            label={labels.flatFare}
            value={safeBands[1]?.flat_fare}
            onChange={(v) => updateField(1, 'flat_fare', v)}
            error={err('bands.1.flat_fare')}
            disabled={!canEdit}
            suffix="€"
            testId="adminv3-pricing-band-1-flat_fare"
          />
          <ReadOnlyChip label="Mode" value="flat" testId="adminv3-pricing-band-1-mode" />
        </BandSection>

        {/* Band 2 — Additive open-tail */}
        <BandSection title={labels.band2}>
          <Field
            label={labels.additiveBase}
            value={safeBands[2]?.additive_base}
            onChange={(v) => updateField(2, 'additive_base', v)}
            error={err('bands.2.additive_base')}
            disabled={!canEdit}
            suffix="€"
            testId="adminv3-pricing-band-2-additive_base"
          />
          <Field
            label={labels.additiveRatePerKm}
            value={safeBands[2]?.additive_rate_per_km}
            onChange={(v) => updateField(2, 'additive_rate_per_km', v)}
            error={err('bands.2.additive_rate_per_km')}
            disabled={!canEdit}
            suffix="€/km"
            testId="adminv3-pricing-band-2-additive_rate_per_km"
          />
          <Field
            label={labels.additiveLowerKm}
            value={safeBands[2]?.additive_lower_km}
            onChange={(v) => updateField(2, 'additive_lower_km', v)}
            error={err('bands.2.additive_lower_km')}
            disabled={!canEdit}
            suffix="km"
            testId="adminv3-pricing-band-2-additive_lower_km"
          />
          <ReadOnlyChip label="Mode" value="additive" testId="adminv3-pricing-band-2-mode" />
          <ReadOnlyChip label={labels.upperKm} value={labels.openTail} testId="adminv3-pricing-band-2-upper_km" />
        </BandSection>

        {/* Hint */}
        <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-[12px] italic text-amber-800" data-testid="adminv3-pricing-bands-hint">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>{labels.hint}</span>
        </div>
      </div>
    </SectionCard>
  );
};

export default BandsEditor;
